import { useScheduleEditorGetItemByIdQuery, useScheduleEditorUpdateItemMutation } from '@graphql/generated';
import { useCallback, useMemo } from 'react';
import { ScheduleEditorData } from '../types';
import { ScheduleFormDisplay, ScheduleFormDisplaySkeleton } from './form/ScheduleFormDisplay';
import React from 'react';
import { EMPTY_FORM_DATA, ScheduleEditorProps } from '../ScheduleEditor';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useDeleteScheduleItem } from '../hooks/useDeleteScheduleItem';
import { formatDateAsISO8601TZ } from './form/hooks/useGetEventTimezone';

export const EditScheduleItem = (props: ScheduleEditorProps & { mode: 'edit' }) => {
  const { scheduleItemId, onSuccess, onError, onCancel, refetch } = props;
  const { deleteScheduleItem } = useDeleteScheduleItem({
    refetch,
  });
  const { data: scheduleQueryResults, loading } = useScheduleEditorGetItemByIdQuery({
    variables: {
      scheduleId: scheduleItemId,
    },
    batchMode: 'fast',
  });
  const [addItem] = useScheduleEditorUpdateItemMutation();

  const handleSubmit = useCallback(
    async (data: ScheduleEditorData) => {
      const results = await addItem({
        variables: {
          payload: {
            scheduleId: scheduleItemId,
            startTimeISO8601TZ: formatDateAsISO8601TZ(data.startTime, data.timezone),
            endTimeISO8601TZ: formatDateAsISO8601TZ(data.endTime, data.timezone),
            description: data.description,
            name: data.name,
            virtualEventLink: data.virtualEventUrl,
            dressCode: data.dressCode,
          },
        },
        refetchQueries: ['ScheduleEditorGetItemById', ...(refetch ?? [])],
      });

      if (results.data?.updateEventScheduleItem) {
        onSuccess(results.data.updateEventScheduleItem);
      } else {
        onError?.(results.errors);
      }
    },
    [addItem, onError, onSuccess, refetch, scheduleItemId],
  );

  const initialData = useMemo(() => {
    const scheduleData = scheduleQueryResults?.getAdminEventScheduleItemById;

    if (!scheduleData) {
      return EMPTY_FORM_DATA;
    }

    const data: ScheduleEditorData = {
      name: scheduleData.name,
      description: scheduleData.description ?? '',
      startTime: scheduleData.startTime ? new Date(scheduleData.startTime?.dateString) : null,
      endTime: scheduleData.endTime ? new Date(scheduleData.endTime?.dateString) : null,
      timezone: scheduleData.timezone?.replace(' ', '_') ?? '',
      virtualEventUrl: scheduleData.virtualEventLink?.urlLinkText ?? null,
      dressCode: scheduleData.dressCode ?? '',
    };

    return data;
  }, [scheduleQueryResults]);

  return (
    <SkeletonGroup
      placeholder={<ScheduleFormDisplaySkeleton />}
      isReady={!loading && !!scheduleQueryResults}
    >
      <ScheduleFormDisplay
        data={initialData}
        onSubmit={handleSubmit}
        onCancel={() => {
          onCancel?.();
        }}
        onDelete={() => {
          deleteScheduleItem(scheduleItemId);
          onCancel?.();
        }}
      />
    </SkeletonGroup>
  );
};
